/**
 * issuanceLayout.js — the titleblock ISSUANCE block, computed in visual-fraction space.
 *
 * The block is a small ruled table (PROJECT NO. / ISSUE DATE / ISSUE TITLE / ZONING NO. /
 * BUILDING NO.) drawn by US as an overlay onto a sheet PDF, positioned by a calibrated
 * titleblock profile. `computeIssuancePrimitives` turns a layout + resolved string values
 * into a flat list of draw primitives whose coordinates are all VISUAL FRACTIONS (top-left
 * origin, 0..1) of the displayed page — so the exact same primitives render on the QC canvas
 * preview (upright) and, via api/_issuanceLayout.js, get reprojected through the page frame
 * and baked with pdf-lib. Preview == final by construction.
 *
 * KEEP THE PURE PART (ISSUANCE_ROWS, makeDefaultLayout, computeIssuancePrimitives) BYTE-FOR-
 * BYTE IN SYNC WITH api/_issuanceLayout.js — api/ and frontend/ can't share imports.
 */

// Disciplines AJ authors (and therefore whose titleblock we own + may overlay). Consultant
// disciplines (Civil / Structural / MEP / Landscape / Plumbing) carry their own titleblocks and
// are never overlaid. Keep in sync with api/_issuanceLayout.js.
export const AJ_DISCIPLINES = new Set([
  'General', 'Special General', 'As-Built', 'Architectural', 'Demolition',
  'Architectural Detailing', 'Interior Architecture', 'Energy',
])

// Sheet-number prefix → discipline (mirrors DrawingSheetList.getDiscipline / _shareDocs guessDiscipline).
const DISC_PREFIX = [
  ['AD', 'Architectural Detailing'], ['AB', 'As-Built'], ['SP', 'Special General'],
  ['EL', 'Electrical'], ['FD', 'General'],
  ['G', 'General'], ['A', 'Architectural'], ['D', 'Demolition'], ['I', 'Interior Architecture'],
  ['E', 'Energy'], ['C', 'Civil'], ['M', 'Mechanical'], ['S', 'Structural'], ['L', 'Landscape'], ['P', 'Plumbing'],
]
export function guessDiscipline(num) {
  const up = String(num || '').trim().toUpperCase()
  for (const [pre, disc] of DISC_PREFIX) { if (up.startsWith(pre)) return disc }
  return 'Other'
}
/** True when a sheet (by its number) is an AJ-authored discipline we may overlay. */
export function isOwnedDiscipline(sheetNumber) {
  return AJ_DISCIPLINES.has(guessDiscipline(sheetNumber))
}

/** Best-matching profile for a page frame by visual size (within tol), or null. */
export function pickProfileForFrame(profiles, frame, visualSizeFn, tol = 0.06) {
  if (!profiles || !profiles.length || !frame) return null
  const { vw, vh } = visualSizeFn(frame)
  let best = null, bestErr = Infinity
  for (const pr of profiles) {
    if (pr.active === false) continue
    const pw = Number(pr.vis_w), ph = Number(pr.vis_h)
    if (!(pw > 0 && ph > 0)) continue
    const ew = Math.abs(pw - vw) / vw, eh = Math.abs(ph - vh) / vh
    if (ew <= tol && eh <= tol) { const e = ew + eh; if (e < bestErr) { bestErr = e; best = pr } }
  }
  return best
}

// Canonical rows + their labels, in titleblock order. `key` matches the resolved-values object.
export const ISSUANCE_ROWS = [
  { key: 'project_no',  label: 'PROJECT NO.' },
  { key: 'issue_date',  label: 'ISSUE DATE' },
  { key: 'issue_title', label: 'ISSUE TITLE' },
  { key: 'zoning_no',   label: 'ZONING NO.' },
  { key: 'building_no', label: 'BUILDING NO.' },
]

/** A reasonable starting layout (the calibrator overwrites `rect` by dragging). Coordinates are
 *  visual fractions of the displayed page; sizes are fractions of the displayed page HEIGHT. */
export function makeDefaultLayout() {
  return {
    rect: { x: 0.86, y: 0.80, w: 0.12, h: 0.13 },  // bottom-right-ish; calibrated per profile
    rows: ISSUANCE_ROWS.map(r => ({ key: r.key, label: r.label })),
    labelColFrac: 0.52,   // (unused by the renderer directly; reserved for future column rules)
    padXFrac: 0.04,       // inner horizontal padding, fraction of block WIDTH
    fontFrac: 0.44,       // text height as a fraction of ROW height
    lineFrac: 0.0007,     // rule/border thickness as a fraction of page height
    opaque: true,         // paint a white background so it cleanly covers any drifting Revit text
  }
}

const clean = v => (v === undefined || v === null || String(v).trim() === '') ? '-' : String(v)

/**
 * Pure layout → primitives (visual fractions). Primitive types:
 *   { type:'fillrect', x,y,w,h }                     white background
 *   { type:'rect',     x,y,w,h, thickness }          border
 *   { type:'line',     x1,y1,x2,y2, thickness }      inter-row rules
 *   { type:'text',     x,y, text, size, align, weight }  x,y = anchor; y is the row CENTER
 * `size`, `thickness` are fractions of the displayed page height. `align` = 'left'|'right'.
 */
export function computeIssuancePrimitives(layout, values) {
  const L = layout || makeDefaultLayout()
  const rect = L.rect || { x: 0.86, y: 0.8, w: 0.12, h: 0.13 }
  const rows = (L.rows && L.rows.length) ? L.rows : ISSUANCE_ROWS
  const n = rows.length || 1
  const rowH = rect.h / n
  const padX = (L.padXFrac != null ? L.padXFrac : 0.04) * rect.w
  const size = (L.fontFrac != null ? L.fontFrac : 0.44) * rowH
  const thickness = (L.lineFrac != null ? L.lineFrac : 0.0007)
  const prims = []

  if (L.opaque !== false) prims.push({ type: 'fillrect', x: rect.x, y: rect.y, w: rect.w, h: rect.h })
  // Horizontal rules only (top + bottom) — no vertical side borders, per Matt's request.
  prims.push({ type: 'line', x1: rect.x, y1: rect.y, x2: rect.x + rect.w, y2: rect.y, thickness })
  prims.push({ type: 'line', x1: rect.x, y1: rect.y + rect.h, x2: rect.x + rect.w, y2: rect.y + rect.h, thickness })

  rows.forEach((r, i) => {
    const ry = rect.y + i * rowH
    if (i > 0) prims.push({ type: 'line', x1: rect.x, y1: ry, x2: rect.x + rect.w, y2: ry, thickness })
    const cy = ry + rowH / 2
    prims.push({ type: 'text', x: rect.x + padX,            y: cy, text: r.label,               size, align: 'left',  weight: 'normal' })
    prims.push({ type: 'text', x: rect.x + rect.w - padX,   y: cy, text: clean(values[r.key]),  size, align: 'right', weight: 'bold'   })
  })
  return prims
}

/**
 * Draw the primitives onto a 2D canvas context, in UPRIGHT visual space. The QC composite
 * rasterizes the page already rotation-applied (upright), so visual fractions map directly to
 * the raster rectangle — no frame math needed here.
 * @param {CanvasRenderingContext2D} ctx
 * @param {object[]} prims
 * @param {{ox:number, oy:number, w:number, h:number}} area  top-left + pixel size of the page area
 * @param {string} [color]  ink color (default titleblock black)
 */
export function drawIssuanceCanvas(ctx, prims, area, color = '#111111') {
  const { ox, oy, w, h } = area
  const X = fx => ox + fx * w
  const Y = fy => oy + fy * h
  ctx.save()
  for (const p of prims) {
    if (p.type === 'fillrect') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(X(p.x), Y(p.y), p.w * w, p.h * h)
    } else if (p.type === 'rect') {
      ctx.strokeStyle = color
      ctx.lineWidth = Math.max(0.5, p.thickness * h)
      ctx.strokeRect(X(p.x), Y(p.y), p.w * w, p.h * h)
    } else if (p.type === 'line') {
      ctx.strokeStyle = color
      ctx.lineWidth = Math.max(0.5, p.thickness * h)
      ctx.beginPath(); ctx.moveTo(X(p.x1), Y(p.y1)); ctx.lineTo(X(p.x2), Y(p.y2)); ctx.stroke()
    } else if (p.type === 'text') {
      const sizePx = Math.max(4, p.size * h)
      ctx.fillStyle = color
      ctx.textAlign = p.align === 'right' ? 'right' : 'left'
      ctx.textBaseline = 'middle'
      ctx.font = (p.weight === 'bold' ? '600 ' : '400 ') + sizePx + 'px Helvetica, Arial, sans-serif'
      ctx.fillText(p.text, X(p.x), Y(p.y))
    }
  }
  ctx.restore()
}
